import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Callout, DirectionalHint, Icon, TooltipHost } from "@fluentui/react";
import { t } from "i18next";
import themeContext from "../../../../context/theme/ThemeContext";
import { getLibraryCatalog, loadLibraryCatalog, subscribeLibraryCatalog } from "./libraryTagStore";
import "./libraryTag.css";

const EMPTY_TAGS = [];
const NAME_LIMIT = 10;

const limitedName = (name) => {
  const value = name == null ? "" : String(name);
  if (value.length <= NAME_LIMIT) return value;
  return `${value.slice(0, NAME_LIMIT)}…`;
};

const TagPill = ({ tag }) => {
  const full = tag.name == null ? "" : String(tag.name);
  const label = <span className="library-row-tag-pill">{limitedName(full)}</span>;
  if (full.length <= NAME_LIMIT) return label;
  return (
    <TooltipHost content={full} id={`library-tag-${tag.id}`}>
      {label}
    </TooltipHost>
  );
};

const LibraryStudyTags = ({ data }) => {
  const { themeName } = useContext(themeContext);
  const [catalog, setCatalog] = useState(getLibraryCatalog);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => subscribeLibraryCatalog(setCatalog), []);
  useEffect(() => {
    loadLibraryCatalog();
  }, []);

  const studyTags = data.row.original.libraryTags || EMPTY_TAGS;

  const groups = useMemo(() => {
    const real = [...catalog.categories]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({
        id: category.id,
        name: category.name,
        virtual: false,
        tags: studyTags.filter((tag) => category.tagIds.includes(tag.id)),
      }))
      .filter((group) => group.tags.length > 0);
    const loose = studyTags.filter((tag) => !catalog.categories.some((category) => category.tagIds.includes(tag.id)));
    const uncategorized = loose.length > 0
      ? [{ id: "uncategorized", name: t("study-list.library-tag.uncategorized"), virtual: true, tags: loose }]
      : [];
    return [...uncategorized, ...real];
  }, [catalog.categories, studyTags]);

  const sortedTags = [...studyTags].sort((a, b) => a.name.localeCompare(b.name));
  const shownTags = sortedTags.slice(0, 3);
  const hiddenTags = sortedTags.slice(3);
  const more = hiddenTags.length;
  const categoryCount = groups.filter((group) => !group.virtual).length;
  const categoryLabel = categoryCount === 1
    ? t("study-list.library-tag.one-category")
    : t("study-list.library-tag.category-count", { count: categoryCount });
  const popTitle = studyTags.length === 1
    ? t("study-list.library-tag.one-tag-by-category")
    : t("study-list.library-tag.tags-by-category", { count: studyTags.length });

  return (
    <div className="library-row-tags">
      <button
        type="button"
        ref={buttonRef}
        className={`library-row-tags-btn${open ? " is-open" : ""}`}
        disabled={studyTags.length === 0}
        aria-expanded={open}
        aria-label={t("study-list.library-tag.show-categories")}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        {studyTags.length === 0 ? (
          <span className="library-row-tags-empty">{t("study-list.library-tag.no-tags")}</span>
        ) : (
          <>
            <span className="library-row-tags-pills">
              {shownTags.map((tag) => (
                <TagPill key={tag.id} tag={tag} />
              ))}
              {more > 0 ? (
                <TooltipHost
                  content={hiddenTags.map((tag) => tag.name).join(", ")}
                  id={`library-tag-more-${data.row.original.id}`}
                  styles={{ root: { display: "inline-flex" } }}
                >
                  <span className="library-row-tag-more">+{more}</span>
                </TooltipHost>
              ) : null}
            </span>
            <span className="library-row-tag-meta">
              {categoryCount > 0 ? categoryLabel : null}
              <span className={`library-row-tag-chevron${open ? " is-open" : ""}`}>
                <Icon iconName="ChevronDown" />
              </span>
            </span>
          </>
        )}
      </button>
      {open ? (
        <Callout
          target={buttonRef.current}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          gapSpace={4}
          calloutWidth={320}
          onDismiss={() => setOpen(false)}
          styles={{ root: { zIndex: 1000000 } }}
        >
        <div className="library-row-tag-pop" data-theme={themeName}>
          <div className="library-row-tag-pop-title">{popTitle}</div>
          {groups.map((group) => (
            <div key={group.id} className="library-row-tag-group">
              <div className={`library-row-tag-group-name${group.virtual ? " is-virtual" : ""}`}>
                <span className="library-row-tag-group-label">{group.name}</span>
                <span className="library-row-tag-group-count">
                  {group.tags.length === 1
                    ? t("study-list.library-tag.one-tag")
                    : t("study-list.library-tag.tag-count", { count: group.tags.length })}
                </span>
              </div>
              <div className="library-row-tag-group-pills">
                {group.tags.map((tag) => (
                  <span key={tag.id} className="library-row-tag-pill">{tag.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        </Callout>
      ) : null}
    </div>
  );
};

export default LibraryStudyTags;
